import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Image, Modal, Row, Table } from "react-bootstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import {
  CustomDropdownContainer,
  FormFieldLabel,
  PageHeader,
  StatusBadge,
  TableSearchField,
} from "CommonComponent";
import CkEditorField from "CommonComponent/CkEditorField/CkEditorField";
import { hideCommonLoader, showCommonLoader } from "actions/commonActions";
import { useObjectViewedId } from "hooks/useObjectViewedId";
import {
  DEFAULT_TEMPLATE_PLACEHOLDERS,
  QUEUE_MODE_OPTIONS,
  fetchEmailTemplate,
  fetchEmailTemplates,
  getErrorMessage,
  updateEmailTemplate,
} from "../emailSettingsApi";
import "../EmailSettings.css";

const notify = (message, type = "general_error") => ({
  type: "SHOW_SNACKBAR",
  snackbar_notification: message,
  snackbar_notification_type: type,
});

/** Avoid clipping by .content-area overflow when menus open near the page bottom. */
const selectMenuProps = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
  styles: {
    menuPortal: (base) => ({ ...base, zIndex: 10050 }),
  },
};

const queueModeLabel = (value) =>
  QUEUE_MODE_OPTIONS.find((o) => o.value === value)?.label || value || "—";

const toForm = (template) => ({
  name: template?.name || "",
  event_code: template?.event_code || "",
  event_label: template?.event_label || "",
  subject: template?.subject || "",
  body_html: template?.body_html || "",
  queue_mode: template?.queue_mode || "queued",
  is_enabled: !!template?.is_enabled,
  is_active: template?.is_active !== false,
  is_system: !!template?.is_system,
  supported_placeholders:
    template?.supported_placeholders?.length > 0
      ? template.supported_placeholders
      : DEFAULT_TEMPLATE_PLACEHOLDERS,
});

const EmailTemplates = () => {
  const dispatch = useDispatch();
  const objectViewedId = useObjectViewedId();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(toForm(null));

  const load = useCallback(async () => {
    dispatch(showCommonLoader());
    try {
      setRows(await fetchEmailTemplates(objectViewedId));
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    } finally {
      dispatch(hideCommonLoader());
    }
  }, [dispatch, objectViewedId]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      [row.name, row.event_code, row.event_label, row.subject, row.template_type]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, filter]);

  const openEdit = async (row) => {
    setEditingId(row.id);
    setForm(toForm(row));
    try {
      const detail = await fetchEmailTemplate(row.id, objectViewedId);
      if (detail?.id) {
        setForm(toForm(detail));
      }
    } catch (error) {
      // Non-blocking — the list row already carries every editable field.
    }
  };

  const closeEdit = () => {
    setEditingId(null);
    setForm(toForm(null));
  };

  const save = async () => {
    if (!editingId) return;
    if (!form.name.trim() || !form.subject.trim() || !form.body_html.trim()) {
      dispatch(notify("Name, subject, and body are required."));
      return;
    }

    dispatch(showCommonLoader());
    try {
      await updateEmailTemplate(
        editingId,
        {
          name: form.name,
          subject: form.subject,
          body_html: form.body_html,
          queue_mode: form.queue_mode,
          is_enabled: !!form.is_enabled,
          is_active: !!form.is_active,
        },
        objectViewedId
      );
      dispatch(notify("Email template saved.", "success"));
      closeEdit();
      await load();
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    } finally {
      dispatch(hideCommonLoader());
    }
  };

  const copyPlaceholder = (placeholder) => {
    const token = `{{${placeholder}}}`;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard
        .writeText(token)
        .then(() => dispatch(notify(`${token} copied.`, "success")))
        .catch(() => {});
    }
  };

  return (
    <div className="email-settings-page">
      <PageHeader title="Email Templates" />

      <div className="main-content-div page-card">
        <p className="email-settings-hint mb-3">
          Email templates are managed by the application. You can configure the
          available templates without changing their system event codes.
        </p>
        <TableSearchField
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="mb-3"
        />
        <Table responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Event Code</th>
              <th>Type</th>
              <th>Enabled</th>
              <th>Queue Mode</th>
              <th>Status</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7}>No email templates found.</td>
              </tr>
            ) : (
              filtered.map((row) => (
                <tr key={row.id}>
                  <td>{row.name || "—"}</td>
                  <td className="email-settings-mono">{row.event_code || "—"}</td>
                  <td>{row.template_type || (row.is_system ? "System" : "Custom")}</td>
                  <td>
                    <StatusBadge
                      label={row.is_enabled ? "Enabled" : "Disabled"}
                      variant={row.is_enabled ? "active" : "inactive"}
                    />
                  </td>
                  <td>{queueModeLabel(row.queue_mode)}</td>
                  <td>
                    <StatusBadge
                      label={row.is_active ? "Active" : "Inactive"}
                      variant={row.is_active ? "active" : "inactive"}
                    />
                  </td>
                  <td className="form-action-width text-end">
                    <Button
                      variant="none"
                      className="border-0"
                      title="Edit"
                      onClick={() => openEdit(row)}
                    >
                      <Image src="/assets/images/edit-icon.svg" alt="Edit" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={!!editingId} onHide={closeEdit} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Email Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={6}>
              <FormFieldLabel>Template Name</FormFieldLabel>
              <Form.Control
                value={form.name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Template name"
              />
            </Col>
            <Col md={6}>
              <FormFieldLabel>Event Code</FormFieldLabel>
              <div className="email-settings-readonly">{form.event_code || "—"}</div>
            </Col>
          </Row>

          <Row className="g-3 mt-0">
            <Col md={6}>
              <FormFieldLabel>Queue Mode</FormFieldLabel>
              <Select
                className="multi-select"
                classNamePrefix="react-select"
                options={QUEUE_MODE_OPTIONS}
                value={
                  QUEUE_MODE_OPTIONS.find((o) => o.value === form.queue_mode) || null
                }
                onChange={(opt) =>
                  setForm((prev) => ({ ...prev, queue_mode: opt?.value || "queued" }))
                }
                components={{ ValueContainer: CustomDropdownContainer }}
                {...selectMenuProps}
              />
            </Col>
            <Col md={6} className="d-flex align-items-end">
              <div>
                <Form.Check
                  type="switch"
                  id="email-template-enabled"
                  label="Enabled"
                  checked={!!form.is_enabled}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, is_enabled: e.target.checked }))
                  }
                />
                <Form.Check
                  type="switch"
                  id="email-template-active"
                  label="Active"
                  checked={!!form.is_active}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, is_active: e.target.checked }))
                  }
                />
              </div>
            </Col>
          </Row>

          <div className="mt-3">
            <FormFieldLabel>Email Subject</FormFieldLabel>
            <Form.Control
              value={form.subject}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, subject: e.target.value }))
              }
              placeholder="Email subject"
            />
          </div>

          <div className="mt-3">
            <FormFieldLabel>Email Body</FormFieldLabel>
            <CkEditorField
              key={editingId || "email-template-body"}
              data={form.body_html}
              onChange={(html) => setForm((prev) => ({ ...prev, body_html: html }))}
            />
          </div>

          {form.supported_placeholders.length > 0 ? (
            <div className="mt-3">
              <FormFieldLabel>Supported Placeholders</FormFieldLabel>
              <p className="email-settings-hint">
                Insert these in the subject or body; click a chip to copy it.
              </p>
              <div className="email-settings-placeholders">
                {form.supported_placeholders.map((placeholder) => (
                  <span
                    key={placeholder}
                    className="email-settings-chip"
                    role="button"
                    tabIndex={0}
                    onClick={() => copyPlaceholder(placeholder)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        copyPlaceholder(placeholder);
                      }
                    }}
                  >
                    {`{{${placeholder}}}`}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEdit}>
            Cancel
          </Button>
          <Button
            type="button"
            className="n-u-btn"
            onClick={save}
            disabled={
              !form.name.trim() || !form.subject.trim() || !form.body_html.trim()
            }
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EmailTemplates;
